import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import message from '../../utils/message';
import UserHeaderBar from '../../components/userHeaderBar/userHeaderBar';
import { getPackage } from '../../reduxStore/actions/packageActionsHelper';
import { packages$, packageLoading$, packageError$ } from '../../reduxStore/selectors/packageSelectors';
import { usersData$ } from '../../reduxStore/selectors/userSelectors';
import styles from '../../publicAsset/userPackageSelectionPage.module.scss';

const UserPackageSelectionPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const packages = useSelector(packages$);
    const loading = useSelector(packageLoading$);
    const error = useSelector(packageError$);
    const userData = useSelector(usersData$);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [currentSlide, setCurrentSlide] = useState(0);
    const carouselRef = useRef(null);

    useEffect(() => {
        // Fetch all packages when component mounts
        dispatch(getPackage.request());
    }, [dispatch]);

    useEffect(() => {
        // Pre-select user's current package if they have one
        if (userData?.selected_package && packages) {
            setSelectedPackage(userData.selected_package);
        }
    }, [userData, packages]);

    const handleSelectPackage = (packageId) => {
        setSelectedPackage(packageId);
    };

    const handleSubscribe = (pkg) => {
        if (!pkg) return;

        // Check if it's the current package
        if (isCurrentPackage(pkg.id)) {
            message.info('This is your current plan');
            return;
        }

        // Navigate to checkout page with package data
        navigate('/user-checkout', { state: { package: pkg } });
    };

    const formatPrice = (price) => {
        if (price === null || price === undefined) return '0';
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        }).format(price);
    };

    const isCurrentPackage = (packageId) => {
        return userData?.selected_package === packageId;
    };

    // Sort packages by price or order
    const sortedPackages = packages ? [...packages].sort((a, b) =>
        (a.package_order || 0) - (b.package_order || 0)
    ) : [];

    // Separate current package from other packages
    const currentPackage = sortedPackages.find(pkg => isCurrentPackage(pkg.id));
    const otherPackages = sortedPackages.filter(pkg => !isCurrentPackage(pkg.id));

    // Carousel navigation
    const nextSlide = () => {
        if (currentSlide < otherPackages.length - 1) {
            setCurrentSlide(currentSlide + 1);
        }
    };

    const prevSlide = () => {
        if (currentSlide > 0) {
            setCurrentSlide(currentSlide - 1);
        }
    };

    const goToSlide = (index) => {
        setCurrentSlide(index);
    };

    return (
        <>
            <Helmet>
                <title>Choose Your Plan | Prepal</title>
                <meta name="description" content="Select the perfect plan for your SAT preparation journey." />
            </Helmet>

            <div className={styles.packageSelectionPage}>
                <UserHeaderBar />

                <div className={styles.content}>
                    {/* Header Section */}
                    <div className={styles.header}>
                        <h1 className={styles.title}>Choose Your Perfect Plan</h1>
                        <p className={styles.subtitle}>
                            Unlock your full potential with our comprehensive SAT preparation packages
                        </p>
                    </div>

                    {/* Loading State */}
                    {loading && (
                        <div className={styles.loadingContainer}>
                            <div className={styles.spinner}></div>
                            <p>Loading available packages...</p>
                        </div>
                    )}

                    {/* Error State */}
                    {error && (
                        <div className={styles.errorContainer}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="10" stroke="#dc464f" strokeWidth="2" />
                                <path d="M12 8v4M12 16h.01" stroke="#dc464f" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                            <h3>Unable to load packages</h3>
                            <p>{error}</p>
                            <button onClick={() => dispatch(getPackage.request())}>Try Again</button>
                        </div>
                    )}

                    {/* Packages Grid */}
                    {!loading && !error && sortedPackages.length > 0 && (
                        <div className={styles.packagesLayout}>
                            {/* Current Package Display (Fixed) */}
                            {currentPackage && (
                                <div className={styles.currentPackageSection}>
                                    <h2 className={styles.sectionTitle}>Your Current Plan</h2>
                                    <div className={styles.currentPackageCard}>


                                        {currentPackage.discount_percent > 0 && (
                                            <div className={styles.discountBadge}>
                                                {currentPackage.discount_percent}% OFF
                                            </div>
                                        )}

                                        <div className={styles.packageHeader}>
                                            <div className={styles.packageIcon}>
                                                <svg width="18" height="18" viewBox="0 0 22 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </div>
                                            <h3 className={styles.packageName}>{currentPackage.name}</h3>
                                            {currentPackage.description && (
                                                <p className={styles.packageDescription}>{currentPackage.description}</p>
                                            )}
                                        </div>

                                        <div className={styles.pricing}>
                                            <div className={styles.price}>
                                                <span className={styles.amount}>{formatPrice(currentPackage.price_vnd)}</span>
                                                <span className={styles.currency}>VND</span>
                                            </div>
                                            <div className={styles.duration}>
                                                per {currentPackage.duration_value} {currentPackage.duration_type}
                                            </div>
                                        </div>

                                        {currentPackage.features && currentPackage.features.length > 0 && (
                                            <div className={styles.features}>
                                                <div className={styles.featuresTitle}>What's included:</div>
                                                <ul className={styles.featuresList}>
                                                    {currentPackage.features.map((feature, index) => (
                                                        <li key={index} className={styles.featureItem}>
                                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M20 6L9 17l-5-5" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                            </svg>
                                                            <span>{feature}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        <button className={`${styles.selectButton} ${styles.currentButton}`} disabled>
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                                            </svg>
                                            <span>Current Plan</span>
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Other Packages Carousel */}
                            {otherPackages.length > 0 && (
                                <div className={styles.carouselSection}>
                                    <h2 className={styles.sectionTitle}>
                                        {currentPackage ? 'Upgrade Your Plan' : 'Available Plans'}
                                    </h2>

                                    <div className={styles.carouselWrapper}>
                                        {/* Previous Button */}
                                        {otherPackages.length > 1 && (
                                            <button
                                                className={`${styles.carouselButton} ${styles.prevButton}`}
                                                onClick={prevSlide}
                                                disabled={currentSlide === 0}
                                            >
                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </button>
                                        )}

                                        {/* Carousel Container */}
                                        <div className={styles.carouselContainer} ref={carouselRef}>
                                            <div className={styles.carouselTrack} style={{
                                                transform: `translateX(-${currentSlide * 100}%)`
                                            }}>
                                                {otherPackages.map((pkg) => (
                                                    <div
                                                        key={pkg.id}
                                                        className={`${styles.packageCard} ${styles.carouselCard} ${selectedPackage === pkg.id ? styles.selected : ''
                                                            }`}
                                                        onClick={() => handleSelectPackage(pkg.id)}
                                                    >
                                                        {pkg.discount_percent > 0 && (
                                                            <div className={styles.discountBadge}>
                                                                {pkg.discount_percent}% OFF
                                                            </div>
                                                        )}

                                                        <div className={styles.packageHeader}>
                                                            <div className={styles.packageIcon}>
                                                                <svg width="32" height="32" viewBox="0 0 22 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                    <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                </svg>
                                                            </div>
                                                            <h3 className={styles.packageName}>{pkg.name}</h3>
                                                            {pkg.description && (
                                                                <p className={styles.packageDescription}>{pkg.description}</p>
                                                            )}
                                                        </div>

                                                        <div className={styles.pricing}>
                                                            <div className={styles.price}>
                                                                <span className={styles.amount}>{formatPrice(pkg.price_vnd)}</span>
                                                                <span className={styles.currency}>VND</span>
                                                            </div>
                                                            <div className={styles.duration}>
                                                                per {pkg.duration_value} {pkg.duration_type}
                                                            </div>
                                                        </div>

                                                        {pkg.features && pkg.features.length > 0 && (
                                                            <div className={styles.features}>
                                                                <div className={styles.featuresTitle}>What's included:</div>
                                                                <ul className={styles.featuresList}>
                                                                    {pkg.features.map((feature, index) => (
                                                                        <li key={index} className={styles.featureItem}>
                                                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                                <path d="M20 6L9 17l-5-5" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                            </svg>
                                                                            <span>{feature}</span>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )}

                                                        <button
                                                            className={styles.selectButton}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleSubscribe(pkg);
                                                            }}
                                                        >
                                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M12 19V5M5 12l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                            </svg>
                                                            <span>Select Plan</span>
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Next Button */}
                                        {otherPackages.length > 1 && (
                                            <button
                                                className={`${styles.carouselButton} ${styles.nextButton}`}
                                                onClick={nextSlide}
                                                disabled={currentSlide === otherPackages.length - 1}
                                            >
                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>

                                    {/* Carousel Indicators */}
                                    {otherPackages.length > 1 && (
                                        <div className={styles.carouselIndicators}>
                                            {otherPackages.map((_, index) => (
                                                <button
                                                    key={index}
                                                    className={`${styles.indicator} ${currentSlide === index ? styles.active : ''
                                                        }`}
                                                    onClick={() => goToSlide(index)}
                                                    aria-label={`Go to slide ${index + 1}`}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Empty State */}
                    {!loading && !error && sortedPackages.length === 0 && (
                        <div className={styles.emptyState}>
                            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z" stroke="#d9d9d9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <h3>No packages available</h3>
                            <p>Please check back later for available subscription plans.</p>
                        </div>
                    )}

                    {/* Info Section */}
                    <div className={styles.infoSection}>
                        <div className={styles.infoCard}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z" stroke="#7d7fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <div>
                                <h4>Secure Payment</h4>
                                <p>Your payment information is encrypted and secure</p>
                            </div>
                        </div>
                        <div className={styles.infoCard}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="10" stroke="#7d7fff" strokeWidth="2" />
                                <path d="M12 6v6l4 2" stroke="#7d7fff" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                            <div>
                                <h4>Cancel Anytime</h4>
                                <p>No long-term commitments. Cancel your subscription whenever you want</p>
                            </div>
                        </div>
                        <div className={styles.infoCard}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="10" stroke="#7d7fff" strokeWidth="2" />
                                <path d="M9 12l2 2 4-4" stroke="#7d7fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <div>
                                <h4>Instant Access</h4>
                                <p>Get immediate access to all features after subscription</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default UserPackageSelectionPage;
